import type { OnTrack } from "@retirement-planner/contract";
import { useLanguage } from "../i18n/LanguageProvider";
import { formatProbability } from "../lib/format";

const ICON: Record<OnTrack, string> = { on_track: "●", caution: "▲", behind: "■" };

/**
 * The dashboard's answer to "can I retire, and when?" — verdict + probability + age.
 * Status is conveyed by word AND icon, never color alone (WCAG 1.4.1); the region is
 * aria-live so a recompute announces the new verdict.
 */
export function OnTrackHeadline({
  onTrack,
  probability,
  projectedRetirementAge,
  planToAge,
  loading = false,
}: {
  onTrack: OnTrack;
  probability: number;
  projectedRetirementAge?: number | null;
  planToAge: number;
  loading?: boolean;
}) {
  const { t, locale } = useLanguage();
  if (loading) {
    return <div className="skeleton" style={{ height: 40, maxWidth: 420 }} role="status" aria-label={t("loading")} />;
  }
  const label = onTrack === "on_track" ? t("onTrack") : onTrack === "caution" ? t("caution") : t("behind");
  return (
    <div aria-live="polite">
      <p className={`headline status--${onTrack}`}>
        <span className="status-dot" aria-hidden="true" />
        <span aria-hidden="true">{ICON[onTrack]}</span>
        <span>{label}</span>
        <span style={{ fontWeight: 400, fontSize: "var(--font-size-body)" }}>
          — {formatProbability(probability, locale)} · {t("probabilityLast")} {planToAge}
        </span>
      </p>
      {projectedRetirementAge != null && (
        <p style={{ color: "var(--color-text-secondary)", margin: 0 }}>
          {t("projection")} {projectedRetirementAge}
        </p>
      )}
    </div>
  );
}
